'use client';
import React from 'react';
import Icon from '@/components/ui/AppIcon';
import type { DashboardData } from '../page';

interface Props {
  data: DashboardData;
}

export default function AlertCardsRow({ data }: Props) {
  const { employees, payrollRecords, onboarding, candidates } = data;

  // Onboarding pipeline: employees with Onboarding status + active onboarding records
  const onboardingCount = employees.filter((e) => e.employee_status === 'Onboarding').length;

  // Payroll holds
  const payrollHolds = payrollRecords.filter((p) => p.payroll_status === 'On Hold').length;

  // Right to Work: employees without RTW verified (right_to_work = false)
  const rtwPending = employees.filter((e) => e.right_to_work === false).length;

  // Active employees count for shift coverage placeholder
  const activeCount = employees.filter((e) => e.employee_status === 'Active').length;

  const alertCards = [
    {
      id: 'alert-hmrc-rti',
      title: 'HMRC RTI Deadline',
      value: '12 days',
      sub: 'Tax Month 3 · FPS due 19 Jun',
      icon: 'DocumentTextIcon',
      borderColor: 'rgba(245,158,11,0.4)',
      bgColor: 'rgba(245,158,11,0.06)',
      iconBg: 'rgba(245,158,11,0.12)',
      textColor: 'var(--color-amber)',
      badge: 'WARNING',
      badgeStyle: 'status-warning',
    },
    {
      id: 'alert-onboarding',
      title: 'Onboarding Pipeline',
      value: onboardingCount > 0 ? `${onboardingCount} pending` : 'None',
      sub: onboardingCount > 0 ? `${onboardingCount} employee(s) in onboarding` : 'No active onboarding',
      icon: 'UserPlusIcon',
      borderColor: 'rgba(139,92,246,0.4)',
      bgColor: 'rgba(139,92,246,0.06)',
      iconBg: 'rgba(139,92,246,0.12)',
      textColor: 'var(--color-purple)',
      badge: 'PIPELINE',
      badgeStyle: 'status-onboarding',
    },
    {
      id: 'alert-payroll-holds',
      title: 'Payroll Holds',
      value: payrollHolds > 0 ? `${payrollHolds} hold${payrollHolds !== 1 ? 's' : ''}` : 'None',
      sub: payrollHolds > 0 ? `${payrollHolds} payroll record(s) on hold` : 'All payroll records clear',
      icon: 'ExclamationTriangleIcon',
      borderColor: payrollHolds > 0 ? 'rgba(239,68,68,0.4)' : 'rgba(16,185,129,0.4)',
      bgColor: payrollHolds > 0 ? 'rgba(239,68,68,0.06)' : 'rgba(16,185,129,0.06)',
      iconBg: payrollHolds > 0 ? 'rgba(239,68,68,0.12)' : 'rgba(16,185,129,0.12)',
      textColor: payrollHolds > 0 ? 'var(--color-red)' : 'var(--color-green)',
      badge: payrollHolds > 0 ? 'ACTION' : 'CLEAR',
      badgeStyle: payrollHolds > 0 ? 'status-critical' : 'status-healthy',
    },
    {
      id: 'alert-employees',
      title: 'Active Employees',
      value: `${activeCount}`,
      sub: `${employees.length} total · ${rtwPending} RTW pending`,
      icon: 'UserGroupIcon',
      borderColor: 'rgba(59,130,246,0.4)',
      bgColor: 'rgba(59,130,246,0.06)',
      iconBg: 'rgba(59,130,246,0.12)',
      textColor: 'var(--color-blue)',
      badge: 'ACTIVE',
      badgeStyle: 'status-active',
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      {alertCards.map((card) => (
        <div
          key={card.id}
          className="rounded-xl p-4 space-y-3 cursor-pointer hover:scale-[1.01] transition-transform duration-150"
          style={{ background: card.bgColor, border: `1px solid ${card.borderColor}` }}
        >
          <div className="flex items-center justify-between">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: card.iconBg }}>
              <Icon name={card.icon as any} size={18} style={{ color: card.textColor } as React.CSSProperties} />
            </div>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${card.badgeStyle}`}>
              {card.badge}
            </span>
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground">{card.title}</p>
            <p className="text-2xl font-bold tabular-nums mt-0.5" style={{ color: card.textColor }}>{card.value}</p>
            <p className="text-xs text-muted-foreground mt-1">{card.sub}</p>
          </div>
        </div>
      ))}
    </div>
  );
}